import { Minus, TrendingDown, TrendingUp, type LucideIcon } from 'lucide-react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';
import { Skeleton } from './Skeleton.tsx';
import './Card.css';

export interface CardProps extends ComponentPropsWithoutRef<'div'> {
  as?: 'div' | 'section' | 'article';
  /** Hover lift for clickable cards. */
  interactive?: boolean;
  padding?: 'none' | 'sm' | 'md';
}

export function Card({ as: Tag = 'div', interactive = false, padding = 'md', className, ...rest }: CardProps) {
  return <Tag {...rest} className={cx('card', interactive && 'interactive', padding !== 'md' && `card--pad-${padding}`, className)} />;
}

export interface CardHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  actions?: ReactNode;
  titleAs?: 'h2' | 'h3';
  titleId?: string;
  className?: string;
}

export function CardHeader({ title, description, icon: Icon, actions, titleAs: Title = 'h2', titleId, className }: CardHeaderProps) {
  return (
    <header className={cx('card-header', className)}>
      {Icon && (
        <span className="card-header__icon" aria-hidden="true">
          <Icon size={18} strokeWidth={1.75} />
        </span>
      )}
      <div className="card-header__text">
        <Title id={titleId} className="card-title">
          {title}
        </Title>
        {description && <p className="card-description">{description}</p>}
      </div>
      {actions && <div className="card-header__actions">{actions}</div>}
    </header>
  );
}

export interface HeroCardProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Large figure shown on the right (desktop) or below (phone). */
  aside?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
  titleAs?: 'h1' | 'h2';
}

/** Brand-gradient summary card. DESIGN.md: only one per viewport, never for ordinary cards. */
export function HeroCard({ eyebrow, title, description, aside, actions, children, className, titleAs: Title = 'h2' }: HeroCardProps) {
  return (
    <section className={cx('card', 'card--hero', className)}>
      <div className="hero__layout">
        <div className="hero__main">
          {eyebrow && <p className="hero__eyebrow">{eyebrow}</p>}
          <Title className="hero__title">{title}</Title>
          {description && <p className="hero__description">{description}</p>}
          {actions && <div className="hero__actions">{actions}</div>}
        </div>
        {aside && <div className="hero__aside">{aside}</div>}
      </div>
      {children}
    </section>
  );
}

export interface Trend {
  direction: 'up' | 'down' | 'flat';
  /** e.g. "+12% จากสัปดาห์ก่อน" */
  label: string;
  /** Whether "up" is good for this metric (false for backlog/failure counts). */
  upIsGood?: boolean;
}

export function TrendChip({ trend }: { trend: Trend }) {
  const Icon = trend.direction === 'up' ? TrendingUp : trend.direction === 'down' ? TrendingDown : Minus;
  const upIsGood = trend.upIsGood ?? true;
  const quality =
    trend.direction === 'flat' ? 'flat' : (trend.direction === 'up') === upIsGood ? 'good' : 'bad';
  return (
    <span className={cx('trend', `trend--${quality}`)}>
      <Icon size={14} strokeWidth={2} aria-hidden="true" />
      <span className="num">{trend.label}</span>
    </span>
  );
}

export type MetricTone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

export interface MetricCardProps {
  label: ReactNode;
  value: ReactNode;
  icon?: LucideIcon;
  tone?: MetricTone;
  trend?: Trend;
  /** Secondary line under the value (caption). */
  hint?: ReactNode;
  loading?: boolean;
  className?: string;
  onClick?: () => void;
}

/** KPI card: label on top, large value, trend chip/hint below (DESIGN.md §9 KPI). */
export function MetricCard({ label, value, icon: Icon, tone = 'neutral', trend, hint, loading = false, className, onClick }: MetricCardProps) {
  const body = (
    <>
      <div className="metric-card__top">
        <p className="metric-card__label">{label}</p>
        {Icon && (
          <span className={cx('metric-card__icon', `metric-card__icon--${tone}`)} aria-hidden="true">
            <Icon size={18} strokeWidth={1.75} />
          </span>
        )}
      </div>
      <div className="metric-card__value" aria-busy={loading || undefined}>
        {loading ? <Skeleton width="45%" height={30} radius={8} /> : value}
      </div>
      <div className="metric-card__foot">
        {loading ? (
          <Skeleton width="60%" height={14} />
        ) : (
          <>
            {trend && <TrendChip trend={trend} />}
            {hint && <span className="metric-card__hint">{hint}</span>}
          </>
        )}
      </div>
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={cx('card', 'interactive', 'metric-card', 'metric-card--button', className)} onClick={onClick}>
        {body}
      </button>
    );
  }
  return <div className={cx('card', 'metric-card', className)}>{body}</div>;
}
